import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Loader2, Clock, CalendarClock, Info, Ban, Users, Minus, Plus } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import type { BookingMode, TimeLimitType } from '@/lib/timeBookingUtils';
import { generateTimePreview } from '@/lib/timeBookingUtils';

interface BookingModeSettingsProps {
  bookingMode: BookingMode;
  bookingIntervalMinutes: 15 | 30 | 60;
  reservationDurationMinutes: number;
  timeLimitType: TimeLimitType;
  maxReservationsPerTime: number | null;
  maxPeoplePerTime: number | null;
  onUpdate: (
    bookingMode: BookingMode,
    bookingIntervalMinutes: 15 | 30 | 60,
    reservationDurationMinutes: number,
    timeLimitType: TimeLimitType,
    maxReservationsPerTime: number | null,
    maxPeoplePerTime: number | null
  ) => Promise<void>;
  disabled?: boolean;
}

const DURATION_OPTIONS = [
  { value: 60, label: '1 heure' },
  { value: 75, label: '1h15' },
  { value: 90, label: '1h30' },
  { value: 105, label: '1h45' },
  { value: 120, label: '2 heures' },
  { value: 135, label: '2h15' },
  { value: 150, label: '2h30' },
  { value: 165, label: '2h45' },
  { value: 180, label: '3 heures' },
];

const INTERVAL_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 heure' },
];

export function BookingModeSettings({
  bookingMode: initialMode,
  bookingIntervalMinutes: initialInterval,
  reservationDurationMinutes: initialDuration,
  timeLimitType: initialLimitType,
  maxReservationsPerTime: initialMaxReservations,
  maxPeoplePerTime: initialMaxPeople,
  onUpdate,
  disabled = false,
}: BookingModeSettingsProps) {
  const [mode, setMode] = useState<BookingMode>(initialMode);
  const [interval, setInterval] = useState<15 | 30 | 60>(initialInterval);
  const [duration, setDuration] = useState<number>(initialDuration);
  const [limitType, setLimitType] = useState<TimeLimitType>(initialLimitType);
  const [maxReservations, setMaxReservations] = useState<number>(initialMaxReservations ?? 4);
  const [maxPeople, setMaxPeople] = useState<number>(initialMaxPeople ?? 20);
  const [isUpdating, setIsUpdating] = useState(false);

  // Sync with props
  useEffect(() => {
    setMode(initialMode);
    setInterval(initialInterval);
    setDuration(initialDuration);
    setLimitType(initialLimitType);
    setMaxReservations(initialMaxReservations ?? 4);
    setMaxPeople(initialMaxPeople ?? 20);
  }, [initialMode, initialInterval, initialDuration, initialLimitType, initialMaxReservations, initialMaxPeople]);

  // Compute effective values based on limit type
  const effectiveMaxReservations = limitType === 'reservations' ? maxReservations : null;
  const effectiveMaxPeople = limitType === 'people' ? maxPeople : null;

  const hasChanges = 
    mode !== initialMode || 
    interval !== initialInterval || 
    duration !== initialDuration ||
    limitType !== initialLimitType ||
    effectiveMaxReservations !== initialMaxReservations ||
    effectiveMaxPeople !== initialMaxPeople;

  const handleSubmit = async () => {
    setIsUpdating(true);
    try {
      await onUpdate(mode, interval, duration, limitType, effectiveMaxReservations, effectiveMaxPeople);
      
      let description = mode === 'slot' 
        ? 'Les réservations fonctionnent par créneaux horaires.' 
        : `Les clients peuvent réserver toutes les ${interval} minutes.`;
      
      if (mode === 'time' && limitType === 'reservations' && effectiveMaxReservations) {
        description += ` Max ${effectiveMaxReservations} réservations par horaire.`;
      } else if (mode === 'time' && limitType === 'people' && effectiveMaxPeople) {
        description += ` Max ${effectiveMaxPeople} personnes par horaire.`;
      }
      
      toast({
        title: 'Mode de réservation mis à jour',
        description,
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour le mode de réservation.',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const timePreview = generateTimePreview(interval);

  return (
    <div className="space-y-6">
      {/* Mode Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm font-medium">Mode de réservation</Label>
        </div>
        
        <RadioGroup
          value={mode}
          onValueChange={(value) => setMode(value as BookingMode)}
          disabled={disabled}
          className="grid gap-3"
        >
          <div className="flex items-start space-x-3">
            <RadioGroupItem value="slot" id="mode-slot" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="mode-slot" className="font-medium cursor-pointer">
                Créneaux horaires
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Les clients choisissent parmi vos créneaux prédéfinis (ex: 11h-14h, 18h-22h)
              </p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <RadioGroupItem value="time" id="mode-time" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="mode-time" className="font-medium cursor-pointer">
                Heures libres
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Les clients choisissent une heure précise dans vos plages horaires
              </p>
            </div>
          </div>
        </RadioGroup>
      </div>

      {/* Time Mode Settings */}
      {mode === 'time' && (
        <div className="space-y-4 pl-6 border-l-2 border-primary/20">
          {/* Interval Selection */}
          <div className="space-y-2">
            <Label htmlFor="interval" className="text-sm flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" />
              Intervalle de réservation
            </Label>
            <Select
              value={interval.toString()}
              onValueChange={(value) => setInterval(parseInt(value) as 15 | 30 | 60)}
              disabled={disabled}
            >
              <SelectTrigger id="interval" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INTERVAL_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value.toString()}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Les clients pourront réserver à : {timePreview.join(', ')}...
            </p>
          </div>

          {/* Duration Selection */}
          <div className="space-y-2">
            <Label htmlFor="duration" className="text-sm">
              Durée de la réservation
            </Label>
            <Select
              value={duration.toString()}
              onValueChange={(value) => setDuration(parseInt(value))}
              disabled={disabled}
            >
              <SelectTrigger id="duration" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value.toString()}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Temps pendant lequel une table est occupée après une réservation
            </p>
          </div>

          {/* Time Limit Type Selection */}
          <div className="space-y-3 pt-2 border-t border-border/50">
            <div className="flex items-center gap-2">
              <Ban className="w-3.5 h-3.5 text-muted-foreground" />
              <Label className="text-sm font-medium">Limite par horaire</Label>
            </div>
            
            <RadioGroup
              value={limitType}
              onValueChange={(value) => setLimitType(value as TimeLimitType)}
              disabled={disabled}
              className="grid gap-3"
            >
              {/* No limit */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="none" id="limit-none" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="limit-none" className="font-medium cursor-pointer">
                    Pas de limite
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Seule la disponibilité des tables limite les réservations
                  </p>
                </div>
              </div>
              
              {/* Limit by reservations */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="reservations" id="limit-reservations" className="mt-1" />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="limit-reservations" className="font-medium cursor-pointer">
                    Nombre de réservations
                  </Label>
                  {limitType === 'reservations' && (
                    <div className="flex items-center gap-3">
                      <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={disabled || maxReservations <= 1} onClick={() => setMaxReservations(Math.max(1, maxReservations - 1))}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="text-2xl font-semibold w-12 text-center">{maxReservations}</span>
                      <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={disabled || maxReservations >= 50} onClick={() => setMaxReservations(Math.min(50, maxReservations + 1))}>
                        <Plus className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground">réservations max</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Limit by people */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="people" id="limit-people" className="mt-1" />
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="limit-people" className="font-medium cursor-pointer">
                      Nombre de personnes
                    </Label>
                    <Users className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  {limitType === 'people' && (
                    <div className="flex items-center gap-3">
                      <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={disabled || maxPeople <= 1} onClick={() => setMaxPeople(Math.max(1, maxPeople - 1))}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="text-2xl font-semibold w-12 text-center">{maxPeople}</span>
                      <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={disabled || maxPeople >= 200} onClick={() => setMaxPeople(Math.min(200, maxPeople + 1))}>
                        <Plus className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground">personnes max</span>
                    </div>
                  )}
                </div>
              </div>
            </RadioGroup>
            
            <p className="text-xs text-muted-foreground">
              Quand un horaire atteint cette limite, il n'apparaît plus dans les choix proposés aux clients.
            </p>
          </div>

          {/* Info Alert */}
          <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-blue-800 dark:text-blue-300 text-xs">
              En mode "Heures libres", vos créneaux horaires définissent les plages 
              pendant lesquelles les clients peuvent réserver. Par exemple, avec un 
              créneau 18h-22h et un intervalle de 30 min, les clients pourront réserver 
              à 18h00, 18h30, 19h00, etc.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Save Button */}
      {hasChanges && (
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isUpdating || disabled}
          className="w-full"
        >
          {isUpdating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Mise à jour...
            </>
          ) : (
            'Enregistrer le mode de réservation'
          )}
        </Button>
      )}
    </div>
  );
}